import { t } from "translations"

import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { toLocalDate } from "@nhi/utils"
import { ISearchHit } from "data/searchManager/interfaces/ISearch"

import Icon from "Components/Icon/Icon"
import Image from "Components/Image/Image"
import Link, { ILinkProps } from "Components/Link"

export default function SearchResult({
    searchResult
}: {
    searchResult: ISearchHit[]
}) {
    return (
        <List>
            {searchResult.map((searchValue: ISearchHit, index) => {
                const showImage =
                    searchValue.ingressImageUrl && index < 5 ? true : false
                const showIngress = index < 5 ? true : false

                if (searchValue.isContentMarketing)
                    return (
                        <ContentMarketingListITem
                            key={searchValue.documentId}
                            searchValue={searchValue}
                            showImage={showImage}
                        />
                    )
                return (
                    <RegularListItem
                        key={searchValue.documentId}
                        searchValue={searchValue}
                        showImage={showImage}
                        showIngress={showIngress}
                    />
                )
            })}
        </List>
    )
}

function ContentMarketingListITem({
    searchValue,
    showImage
}: {
    searchValue: ISearchHit
    showImage: boolean
}) {
    return (
        <ListItem disablePadding>
            <StyledCMCard>
                <StyledCMBanner>
                    <StyledCMOverline
                        variant="overline"
                        color="inherit">
                        {searchValue.isProfessionalContent
                            ? t.contentMarketing.adProfessional
                            : t.contentMarketing.ad}
                    </StyledCMOverline>
                </StyledCMBanner>
                <StyledCMResultListItemButton
                    hasimage={showImage ? "true" : "false"}
                    LinkComponent={Link}
                    href={searchValue.url}>
                    <StyledCMListItemText>
                        <StyledCMTopic
                            variant="subtitle1"
                            lineHeight={1.2}>
                            {searchValue.isProfessionalContent ? (
                                <>
                                    <b>{t.contentMarketing.ad}</b>{" "}
                                    {" | " + searchValue.topic}{" "}
                                </>
                            ) : (
                                searchValue.topic
                            )}
                        </StyledCMTopic>
                        <Typography variant="h3">
                            {searchValue.title}
                        </Typography>

                        <StyledIngress variant="body2">
                            {searchValue.ingress}
                        </StyledIngress>

                        <StyledCampaignCode variant="body2">
                            {searchValue.approvalCode}
                        </StyledCampaignCode>
                    </StyledCMListItemText>

                    {showImage && (
                        <StyledImage
                            iscontentmarketing={true.toString()}
                            src={searchValue.ingressImageUrl}
                            alt={searchValue.title}
                            fill
                        />
                    )}
                </StyledCMResultListItemButton>
            </StyledCMCard>
        </ListItem>
    )
}

function RegularListItem({
    searchValue,
    showImage,
    showIngress
}: {
    searchValue: ISearchHit
    showImage: boolean
    showIngress: boolean
}) {
    return (
        <ListItem disablePadding>
            <StyledResultListItemButton
                hasimage={showImage ? "true" : "false"}
                LinkComponent={Link}
                href={searchValue.url}>
                <StyledListItemText>
                    <StyledArticleTopic variant="overline">
                        <Icon
                            name="articleOutlined"
                            fontSize="inherit"
                            color="secondary"
                        />
                        {searchValue.template}
                    </StyledArticleTopic>
                    <Typography variant="h3">{searchValue.title}</Typography>

                    {showIngress && (
                        <StyledIngress variant="body2">
                            {searchValue.ingress}
                        </StyledIngress>
                    )}

                    <StyledDateAndTopic
                        variant="subtitle2"
                        component="p">
                        {toLocalDate(
                            searchValue.latestDate,
                            process.env.NEXT_PUBLIC_LOCALE
                        )}{" "}
                        - {searchValue.topic}
                    </StyledDateAndTopic>
                </StyledListItemText>

                {showImage && (
                    <StyledImage
                        iscontentmarketing={false.toString()}
                        src={searchValue.ingressImageUrl}
                        alt={searchValue.title}
                        fill
                    />
                )}
            </StyledResultListItemButton>
        </ListItem>
    )
}

type ListItemProps = ILinkProps & {
    hasimage: string
}

const StyledCMResultListItemButton = styled(ListItemButton)<ListItemProps>(
    ({ theme, hasimage }) => ({
        display: "grid",
        gridTemplateColumns: hasimage === "true" ? "3fr 1fr" : "1fr",
        gap: theme.spacing(2),
        "&.MuiListItemButton-root": {
            marginRight: theme.spacing(-2),
            marginLeft: theme.spacing(-2),
            "&:hover": {
                backgroundColor: "transparent"
            }
        }
    })
)

const StyledResultListItemButton = styled(ListItemButton)<ListItemProps>(
    ({ theme, hasimage }) => ({
        display: "grid",
        gridTemplateColumns: hasimage === "true" ? "3fr 1fr" : "1fr",
        gap: theme.spacing(2),
        "&.MuiListItemButton-root": {
            paddingRight: theme.spacing(2),
            marginRight: theme.spacing(-2),
            paddingLeft: theme.spacing(2),
            marginLeft: theme.spacing(-2)
        }
    })
)

const StyledCMCard = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    width: "fill-available",
    margin: theme.spacing(1, -2),
    border: `${theme.spacing(0.2)} solid ${theme.palette.advertiser.main}`,
    backgroundColor: theme.palette.advertiser[50]
}))

const StyledCMOverline = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white
}))

const StyledCMBanner = styled("div")(({ theme }) => ({
    color: theme.palette.common.white,
    width: "fill-available",
    paddingLeft: theme.spacing(2),
    backgroundColor: theme.palette.advertiser.main
}))

const StyledCMListItemText = styled(ListItemText)(({ theme }) => ({
    gridColumn: 1,
    padding: theme.spacing(0, 2)
}))

const StyledListItemText = styled(ListItemText)(() => ({
    gridColumn: 1
}))

const StyledCMTopic = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
}))

const StyledCampaignCode = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    bottom: 0
}))

const StyledImage = styled(Image)<{ iscontentmarketing: string }>(
    ({ iscontentmarketing: isContentMarketing, theme }) => ({
        marginLeft: "auto",
        marginRight:
            isContentMarketing === "true" ? theme.spacing(4) : theme.spacing(2),
        width: "96px",
        height: "54px",
        objectFit: "cover",
        [theme.breakpoints.up("sm")]: {
            width: "158px",
            height: "89px"
        }
    })
)

const StyledArticleTopic = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
}))

const StyledIngress = styled(Typography)(() => ({
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    lineClamp: "2",
    WebkitBoxOrient: "vertical"
}))

const StyledDateAndTopic = styled(Typography)<{ component: string }>(
    ({ theme }) => ({
        color: theme.palette.text.secondary
    })
)
